'use client'

import { useState, useEffect, useRef, useCallback } from 'react'
import { useAppStore, Step } from '@/hooks/useAppStore'
import { trackLeadRegistered, trackFormCompleted, initAnalytics, collectAllAnalytics, getVisitorId, type AnalyticsData } from '@/lib/analytics'
import { useUTMPersonalization } from '@/hooks/useUTMPersonalization'
import { useFormPersistence } from '@/hooks/useFormPersistence'
import { triggerMilestoneConfetti } from '../Celebrations'
import {
  pushStepEvent,
  pushAbandonmentEvent,
  pushFormStartEvent,
  pushFormCompleteEvent,
  pushPhaseCompleteEvent,
  pushRecoveryEvent
} from '@/lib/datalayer-events'
import { validarEmail, validarTelefone, formatarTelefone } from '../utilitarios'

// Definicao das fases para celebracao
const FASES = [
  { name: 'Contato', steps: [0, 1, 2] },
  { name: 'Projeto', steps: [3, 4, 5] },
  { name: 'Detalhes', steps: [6, 7, 8] },
  { name: 'Finalizar', steps: [9, 10] }
]

export function useQuiz() {
  const {
    currentStep,
    formData,
    setFormData,
    nextStep,
    prevStep,
    setStep,
    setSubmitting,
    setComplete
  } = useAppStore()

  // Estados locais dos campos
  const [nome, setNome] = useState('')
  const [email, setEmail] = useState('')
  const [telefone, setTelefone] = useState('')
  const [empresa, setEmpresa] = useState('')
  const [urlAtual, setUrlAtual] = useState('')
  const [urlReferencia, setUrlReferencia] = useState('')
  const [detalhes, setDetalhes] = useState('')
  const [lgpdAceito, setLgpdAceito] = useState(false)
  const [featuresSelecionadas, setFeaturesSelecionadas] = useState<string[]>([])
  const [coresSelecionadas, setCoresSelecionadas] = useState<string[]>([])
  const [erros, setErros] = useState({ email: '', phone: '' })
  const [validandoWhatsApp, setValidandoWhatsApp] = useState(false)
  const [whatsappVerificado, setWhatsappVerificado] = useState<boolean | null>(null)
  const [formStartTime, setFormStartTime] = useState<number | null>(null)
  const [initialAnalytics, setInitialAnalytics] = useState<AnalyticsData | null>(null)
  const [sessionId] = useState(() =>
    typeof window !== 'undefined'
      ? `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      : ''
  )
  const [showPhaseCelebration, setShowPhaseCelebration] = useState(false)
  const [completedPhaseName, setCompletedPhaseName] = useState('')
  const [maxStepReached, setMaxStepReached] = useState(0)
  const previousStepRef = useRef(currentStep)

  const inputRef = useRef<HTMLInputElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Hook de personalizacao por UTM
  const { personalization, isFromAds } = useUTMPersonalization()

  // Hook de persistencia (Smart Pre-fill)
  const {
    isReturningVisitor,
    returningName,
    lastStep,
    restoreProgress,
    saveProgress,
    clearSavedProgress,
    dismissReturn
  } = useFormPersistence(sessionId)

  // Estado para controlar o modal de boas-vindas
  const [showWelcomeBack, setShowWelcomeBack] = useState(false)
  const [hasRestoredData, setHasRestoredData] = useState(false)

  // Inicializa analytics no mount
  useEffect(() => {
    initAnalytics()
    collectAllAnalytics().then(analytics => {
      setInitialAnalytics(analytics)
    })
  }, [])

  // Mostrar modal de boas-vindas para visitante retornando
  useEffect(() => {
    if (isReturningVisitor && !hasRestoredData && currentStep === Step.INTRO) {
      setShowWelcomeBack(true)
    }
  }, [isReturningVisitor, hasRestoredData, currentStep])

  // Funcao para restaurar dados salvos
  const handleContinueFromSaved = useCallback(() => {
    const data = restoreProgress()
    if (data) {
      setFormData(data.formData)

      if (data.formData.nome) setNome(data.formData.nome)
      if (data.formData.email) setEmail(data.formData.email)
      if (data.formData.whatsapp) setTelefone(data.formData.whatsapp)
      if (data.formData.empresa) setEmpresa(data.formData.empresa)
      if (data.formData.currentSiteUrl) setUrlAtual(data.formData.currentSiteUrl)
      if (data.formData.referenceSites?.[0]) setUrlReferencia(data.formData.referenceSites[0])
      if (data.formData.additional) setDetalhes(data.formData.additional)
      if (data.formData.features) setFeaturesSelecionadas(data.formData.features)
      if (data.formData.brandColors) setCoresSelecionadas(data.formData.brandColors)

      setStep(data.currentStep)
      setMaxStepReached(data.maxStepReached)
      setHasRestoredData(true)

      pushRecoveryEvent(sessionId, data.currentStep)
    }
    setShowWelcomeBack(false)
  }, [restoreProgress, setFormData, setStep, sessionId])

  // Funcao para comecar do zero
  const handleStartOver = useCallback(() => {
    clearSavedProgress()
    dismissReturn()
    setShowWelcomeBack(false)
  }, [clearSavedProgress, dismissReturn])

  // Salvar progresso quando formData ou step mudar
  useEffect(() => {
    if (currentStep > Step.INTRO && currentStep < Step.SUBMITTING) {
      saveProgress(formData, currentStep, maxStepReached)
    }
  }, [formData, currentStep, maxStepReached, saveProgress])

  // Marca inicio do formulario quando sai da intro
  useEffect(() => {
    if (currentStep === Step.NAME && !formStartTime) {
      setFormStartTime(Date.now())
      pushFormStartEvent()
    }
  }, [currentStep, formStartTime])

  // Funcao para salvar lead parcial
  const savePartialLead = useCallback(async () => {
    if (currentStep <= Step.INTRO || currentStep >= Step.SUBMITTING) return
    if (!sessionId) return

    try {
      const analytics = await collectAllAnalytics(formStartTime || undefined)
      const payload = {
        sessionId,
        currentStep,
        maxStepReached,
        formData,
        analytics,
        timeOnPage: formStartTime ? Date.now() - formStartTime : null,
        formStartedAt: formStartTime ? new Date(formStartTime).toISOString() : null
      }

      if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
        navigator.sendBeacon('/api/lead/partial', JSON.stringify(payload))
      } else {
        fetch('/api/lead/partial', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          keepalive: true
        })
      }
    } catch (e) {
      console.error('Erro ao salvar lead parcial:', e)
    }
  }, [sessionId, currentStep, maxStepReached, formData, formStartTime])

  // Track mudanca de step + eventos DataLayer + celebracoes
  useEffect(() => {
    const prevStep = previousStepRef.current
    previousStepRef.current = currentStep

    if (currentStep > maxStepReached) {
      setMaxStepReached(currentStep)
    }

    if (currentStep > prevStep && currentStep > Step.INTRO) {
      pushStepEvent(currentStep, formData as unknown as Record<string, unknown>)
      savePartialLead()

      const prevPhaseIndex = FASES.findIndex(p => p.steps.includes(prevStep))
      const currentPhaseIndex = FASES.findIndex(p => p.steps.includes(currentStep))

      if (prevPhaseIndex !== -1 && currentPhaseIndex !== -1 && currentPhaseIndex > prevPhaseIndex) {
        const completedPhase = FASES[prevPhaseIndex]
        setCompletedPhaseName(completedPhase.name)
        setShowPhaseCelebration(true)
        triggerMilestoneConfetti()
        pushPhaseCompleteEvent(completedPhase.name, prevPhaseIndex)
      }

      if (currentStep === Step.PROFILE && (formData.email || formData.whatsapp)) {
        triggerMilestoneConfetti()
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep])

  // Evento beforeunload para detectar abandono
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (currentStep > Step.INTRO && currentStep < Step.SUCCESS) {
        pushAbandonmentEvent(
          currentStep,
          formStartTime ? Date.now() - formStartTime : 0,
          formData as unknown as Record<string, unknown>
        )
        savePartialLead()
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && currentStep > Step.INTRO && currentStep < Step.SUCCESS) {
        savePartialLead()
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [currentStep, formData, formStartTime, savePartialLead])

  // Focar input quando muda de step
  useEffect(() => {
    setTimeout(() => {
      if (currentStep === Step.DETAILS) {
        textareaRef.current?.focus()
      } else {
        inputRef.current?.focus()
      }
    }, 300)
  }, [currentStep])

  // === Handlers de submit por step ===

  const handleNomeSubmit = useCallback(() => {
    if (nome.trim().length >= 2) {
      setFormData({ nome: nome.trim() })
      nextStep()
    }
  }, [nome, setFormData, nextStep])

  const handleContatoSubmit = useCallback(async () => {
    const emailValido = validarEmail(email)
    const telefoneValido = validarTelefone(telefone)

    if (!emailValido) setErros(prev => ({ ...prev, email: 'Email invalido' }))
    if (!telefoneValido) setErros(prev => ({ ...prev, phone: 'Numero invalido' }))

    if (!emailValido || !telefoneValido) return

    setValidandoWhatsApp(true)
    setErros({ email: '', phone: '' })

    let verificado: boolean | null = null

    try {
      const resposta = await fetch('/api/validar-whatsapp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ telefone }),
      })

      if (resposta.ok) {
        const dados = await resposta.json()

        if (dados.existe === false) {
          setErros(prev => ({
            ...prev,
            phone: 'Este número não tem WhatsApp. Verifique e tente novamente.',
          }))
          setWhatsappVerificado(false)
          setValidandoWhatsApp(false)
          return
        }

        verificado = dados.existe
      }
    } catch {
      // Fallback: permitir envio se API falhar
    }

    setWhatsappVerificado(verificado)
    setValidandoWhatsApp(false)
    setFormData({ email: email.trim().toLowerCase(), whatsapp: telefone, whatsappVerificado: verificado })
    setErros({ email: '', phone: '' })
    trackLeadRegistered()
    nextStep()
  }, [email, telefone, setFormData, nextStep])

  const handlePerfilSubmit = useCallback((tipo: 'pf' | 'pj') => {
    if (tipo === 'pj' && empresa.trim()) {
      setFormData({ demandType: tipo, empresa: empresa.trim() })
      nextStep()
    } else if (tipo === 'pf') {
      setFormData({ demandType: tipo, empresa: '' })
      nextStep()
    }
  }, [empresa, setFormData, nextStep])

  const handleSituacaoSubmit = useCallback((situacao: 'no_site' | 'has_site' | 'rebuild_site') => {
    if (situacao === 'has_site' || situacao === 'rebuild_site') {
      setFormData({ situation: situacao, currentSiteUrl: urlAtual.trim() })
    } else {
      setFormData({ situation: situacao, currentSiteUrl: '' })
    }
    nextStep()
  }, [urlAtual, setFormData, nextStep])

  const handleObjetivoSubmit = useCallback((objetivo: 'leads' | 'sales' | 'portfolio' | 'branding' | 'info') => {
    setFormData({ objective: objetivo })
    nextStep()
  }, [setFormData, nextStep])

  const handleFuncionalidadesSubmit = useCallback(() => {
    setFormData({ features: featuresSelecionadas })
    nextStep()
  }, [featuresSelecionadas, setFormData, nextStep])

  const handleBrandingSubmit = useCallback((temLogo: boolean) => {
    setFormData({
      hasLogo: temLogo,
      brandColors: coresSelecionadas,
      referenceSites: urlReferencia.trim() ? [urlReferencia.trim()] : []
    })
    nextStep()
  }, [coresSelecionadas, urlReferencia, setFormData, nextStep])

  const handleConteudoSubmit = useCallback((status: 'ready' | 'partial' | 'need_help') => {
    setFormData({ contentStatus: status })
    nextStep()
  }, [setFormData, nextStep])

  const handleUrgenciaSubmit = useCallback((urgencia: '7_days' | '15_30_days' | 'negotiate') => {
    setFormData({ urgency: urgencia })
    nextStep()
  }, [setFormData, nextStep])

  const handleDetalhesSubmit = useCallback(() => {
    setFormData({ additional: detalhes.trim(), projectType: 'personalizado' })
    setStep(Step.LGPD)
  }, [detalhes, setFormData, setStep])

  const handleDetalhesSkip = useCallback(() => {
    setFormData({ additional: '', projectType: 'personalizado' })
    setStep(Step.LGPD)
  }, [setFormData, setStep])

  const handleLgpdSubmit = useCallback(async () => {
    if (!lgpdAceito) return

    setFormData({ lgpdConsent: true })
    setStep(Step.SUBMITTING)
    setSubmitting(true)

    try {
      const analytics = initialAnalytics || await collectAllAnalytics(formStartTime || undefined)

      if (initialAnalytics && formStartTime) {
        analytics.session = {
          ...analytics.session,
          timeOnPage: Date.now() - formStartTime,
          formCompletionTime: Date.now() - formStartTime
        }
      }

      const currentFormData = useAppStore.getState().formData
      const visitor_id = getVisitorId()

      const response = await fetch('/api/lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...currentFormData,
          whatsappVerificado,
          lgpdConsent: true,
          analytics,
          sessionId,
          visitor_id
        })
      })

      if (!response.ok) throw new Error('Failed to submit')

      if (typeof window !== 'undefined' && (window as typeof window & { dataLayer?: unknown[] }).dataLayer) {
        const projectValues: Record<string, number> = { simples: 997, institucional: 2497, personalizado: 5000 }
        ;(window as typeof window & { dataLayer?: unknown[] }).dataLayer?.push({
          event: 'generate_lead',
          value: projectValues[currentFormData.projectType || 'simples']
        })
      }

      trackFormCompleted()
      pushFormCompleteEvent(currentFormData as unknown as Record<string, unknown>)
      clearSavedProgress()

      setStep(Step.SUCCESS)
      setComplete(true)
    } catch {
      setStep(Step.LGPD)
    } finally {
      setSubmitting(false)
    }
  }, [lgpdAceito, setFormData, setStep, setSubmitting, setComplete, initialAnalytics, formStartTime, sessionId, clearSavedProgress])

  const toggleFeature = useCallback((id: string) => {
    setFeaturesSelecionadas(prev =>
      prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]
    )
  }, [])

  const toggleColor = useCallback((cor: string) => {
    setCoresSelecionadas(prev =>
      prev.includes(cor) ? prev.filter(c => c !== cor) : prev.length < 4 ? [...prev, cor] : prev
    )
  }, [])

  // Atalho Enter para steps de texto
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        switch (currentStep) {
          case Step.NAME:
            if (nome.trim().length >= 2) handleNomeSubmit()
            break
          case Step.CONTACT:
            if (!validandoWhatsApp) handleContatoSubmit()
            break
        }
      }
    }
    window.addEventListener('keypress', handleKeyPress)
    return () => window.removeEventListener('keypress', handleKeyPress)
  }, [currentStep, nome, handleNomeSubmit, handleContatoSubmit])

  // Mapa de steps para chaves de animacao
  const getStepKey = useCallback(() => {
    const stepMap: Record<number, string> = {
      [Step.INTRO]: 'intro', [Step.NAME]: 'name', [Step.CONTACT]: 'contact', [Step.PROFILE]: 'profile',
      [Step.SITUATION]: 'situation', [Step.OBJECTIVE]: 'objective', [Step.FEATURES]: 'features',
      [Step.BRANDING]: 'branding', [Step.CONTENT]: 'content', [Step.URGENCY]: 'urgency',
      [Step.DETAILS]: 'details', [Step.PROJECT_TYPE]: 'project_type', [Step.LGPD]: 'lgpd',
      [Step.SUBMITTING]: 'submitting', [Step.SUCCESS]: 'success'
    }
    return stepMap[currentStep] || 'intro'
  }, [currentStep])

  return {
    // Estado do quiz
    currentStep,
    formData,
    nextStep,
    prevStep,
    setStep,

    // Campos locais
    nome, setNome,
    email, setEmail,
    telefone, setTelefone: (valor: string) => setTelefone(formatarTelefone(valor)),
    empresa, setEmpresa,
    urlAtual, setUrlAtual,
    urlReferencia, setUrlReferencia,
    detalhes, setDetalhes,
    lgpdAceito, setLgpdAceito,
    featuresSelecionadas,
    coresSelecionadas,
    erros, setErros,
    validandoWhatsApp,
    whatsappVerificado,

    // Refs
    inputRef,
    textareaRef,

    // Handlers
    handleNomeSubmit,
    handleContatoSubmit,
    handlePerfilSubmit,
    handleSituacaoSubmit,
    handleObjetivoSubmit,
    handleFuncionalidadesSubmit,
    handleBrandingSubmit,
    handleConteudoSubmit,
    handleUrgenciaSubmit,
    handleDetalhesSubmit,
    handleDetalhesSkip,
    handleLgpdSubmit,
    toggleFeature,
    toggleColor,

    // Personalizacao UTM
    personalization,
    isFromAds,

    // Welcome back
    showWelcomeBack,
    returningName,
    lastStep,
    handleContinueFromSaved,
    handleStartOver,

    // Celebracoes
    showPhaseCelebration,
    setShowPhaseCelebration,
    completedPhaseName,

    // Utilitarios
    getStepKey
  }
}
